import styled from 'styled-components';
import themeGet from '@styled-system/theme-get';

import Container from '../../Core/Container';
import { AppStoreContainer } from '../AppStores/style';
import Points from '../../../images/pts-icon.svg';

export const StyledContainer = styled(Container)`
  &.pcma-list-section-wrapper {
    width: 100%;
    margin: 0 auto;

    @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
      width: 50%;
      margin: 0;
    }
  }

  &.red-velvet-gradient {
    padding: 40px 0;
    margin: 0 auto;
    @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
      padding: 70px 0;
    }
    @media only screen and (min-width: ${themeGet('breakpoints.xlg')}) {
      padding: 100px 0;
    }
  }

  &.red-velvet-gradient-small {
    padding: 40px 0;

    @media only screen and (min-width: ${themeGet('breakpoints.xlg')}) {
      padding: 50px 0;
    }
  }

  &.security-statement {
    margin-top: 70px;
    margin-bottom: 45px;

    @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
      margin-top: 80px;
      margin-bottom: 70px;
    }
  }

  & ${AppStoreContainer} {
    margin-bottom: 0;
  }
`;

export const Block = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.5rem;

  > div {
    @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
      /* This fixes IE text overflowing parent */
      width: 100%;
    }
  }

  &.stacked-left {
    align-items: flex-start;
  }

  &.side-by-side {
    display: block;

    @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
      > div {
        max-width: 100%;
      }
    }

    @media only screen and (min-width: ${themeGet('breakpoints.xlg')}) {
      display: flex;
      flex-direction: row;
    }

    &.title-with-statement-grey-bkg {
      padding: 2rem 0;

      @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
        display: flex;
        flex-direction: row;
        padding: 4rem 0;
        > div {
          max-width: 50%;
        }
      }
    }

    &.content-focus {
      flex-direction: row;
      display: flex;
      align-items: flex-start;
      margin: 0 auto;

      @media only screen and (max-width: ${themeGet('breakpoints.lg')}) {
        flex-direction: column;
      }

      p {
        &:last-of-type {
          margin: 0;
        }

        & + a {
          margin-top: 1rem;
        }
      }

      & > div {
        max-width: none;
        flex: auto;
        width: auto;
        padding: 0;

        @media only screen and (min-width: 1025px) {
          max-width: 37%;
          width: 100%;
          padding: 0 1.5rem 0 0;
          margin: 0;
        }

        &:last-child {
          padding: 0;

          @media only screen and (min-width: 1025px) {
            max-width: none;
            width: 40%;
          }
        }
      }
    }
  }

  .left {
    text-align: left;
  }

  .center {
    text-align: center;

    &.stacked {
      align-self: center;
    }
  }

  .right {
    text-align: right;
  }

  p {
    margin: 0 0 1rem;

    &:last-of-type {
      margin: 0;
    }
  }

  small {
    font-size: 0.8rem;
    font-style: italic;
    font-weight: ${themeGet('fontWeights.bold')};
  }
`;

export const Heading = styled.div`
  .pcma-heading {
    color: ${themeGet('colors.velvet', '#3D1952')};
  }

  ${StyledContainer}.pcma-list-section & {
    display: flex;
    width: 100%;
    margin: 0 auto;

    h2 {
      color: ${({ theme }) => theme.colors.white};
      font-weight: 500;
      width: 100%;
      text-align: center;
      font-size: 1.8rem;

      @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
        text-align: left;
      }

      @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
        font-size: 2rem;
      }

      span {
        font-size: inherit;
        color: inherit;
      }

      span.points {
        font-size: 3rem;
        display: block;
        margin: 1.25rem 0;
        font-weight: 900;
        margin: 0.5rem 0;
        letter-spacing: -0.5 px;

        @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
          font-size: 4rem;
        }

        &:after {
          content: '';
          background-image: url(${Points});
          background-size: contain;
          background-position: center;
          background-repeat: no-repeat;
          position: relative;
          height: 2.5rem;
          width: 2.5rem;
          display: inline-block;
          bottom: 0;
          right: 0;
          margin-left: 0.25rem;

          @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
            bottom: 1rem;
          }
        }
      }
    }
  }
  ${Block}.red-velvet-gradient & {
    background: -webkit-linear-gradient(
      rgba(59, 25, 81, 1) 35%,
      rgba(221, 59, 76, 1) 65%,
      rgba(225, 37, 27, 1) 100%
    );
    background-attachment: fixed;
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;

    /* Targets firefox */
    @media all and (min--moz-device-pixel-ratio: 0) {
      background-attachment: scroll;
    }
    /* IE & Edge fallback */
    @supports (-ms-ime-align: auto) {
      background-attachment: scroll;
    }

    /* Edge fallback */
    @supports (-ms-accelerator: true) {
      background-attachment: scroll;
    }

    h2,
    h3 {
      font-size: 40px;
      line-height: 50px;
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      color: ${themeGet('colors.pc-red', '#E1251B')}; /*non-webkit fallback*/

      sup {
        font-size: 60%;
        display: inline-block;
        position: static;
        vertical-align: super;
      }
    }
  }

  ${Block}.red-velvet-gradient-small & {
    background: -webkit-linear-gradient(
      rgba(59, 25, 81, 1) 35%,
      rgba(221, 59, 76, 1) 65%,
      rgba(225, 37, 27, 1) 100%
    );
    background-attachment: fixed;
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;

    /* Targets firefox */
    @media all and (min--moz-device-pixel-ratio: 0) {
      background-attachment: scroll;
    }
    /* IE & Edge fallback */
    @supports (-ms-ime-align: auto) {
      background-attachment: scroll;
    }

    /* Edge fallback */
    @supports (-ms-accelerator: true) {
      background-attachment: scroll;
    }

    h2,
    h3 {
      font-size: 30px;
      line-height: 40px;
      text-align: center;
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      color: ${themeGet('colors.pc-red', '#E1251B')}; /*non-webkit fallback*/
    }
  }

  ${Block}.title-with-statement-grey-bkg & {
    h3 {
      font-size: 28px;
    }

    @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
      h3 {
        font-size: 36px;
        font-weight: ${themeGet('fontWeights.medium')};
      }
    }
    @media only screen and (min-width: ${themeGet('breakpoints.xlg')}) {
      h3 {
        font-size: 52px;
      }
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    &.side-by-side {
      width: 100%;
      max-width: 40.5%;
    }

    ${Block}.red-velvet-gradient & {
      h2,
      h3 {
        font-size: 58px;
        line-height: 62px;
      }
    }

    ${Block}.red-velvet-gradient-small & {
      h2,
      h3 {
        font-size: 38px;
        line-height: 56px;
      }
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xlg')}) {
    &.side-by-side {
      width: 100%;
      max-width: 40.5%;
    }

    /* This is the styling for the animated gradient mission statement */
    ${Block}.red-velvet-gradient & {
      h2,
      h3 {
        font-size: 100px;
        line-height: 110px;
      }
    }

    ${Block}.red-velvet-gradient-small & {
      h2,
      h3 {
        font-size: 45px;
        line-height: 60px;
      }
    }
  }
`;

export const Body = styled.div`
  /* width: 100%; */

  ${StyledContainer}.pcma-list-section & {
    margin: 1rem 0;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xlg')}) {
    &.side-by-side {
      width: 100%;
      max-width: 59.5%;
    }
  }

  /* &.stacked {
    max-width: 100%;
  } */

  &.full-width-large-bodycopy {
    max-width: none;

    p {
      font-size: 1.125rem;
      line-height: 2rem;

      @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
        font-size: 1.625rem;
        line-height: 3rem;
      }
    }
  }

  &.centered-large-bodycopy {
    margin: 0 auto;
    max-width: none;

    p {
      font-size: 21.5px;
      letter-spacing: 0;
      line-height: 36px;
      text-align: center;
    }

    @media only screen and (min-width: ${themeGet('breakpoints.sm')}) {
      p {
        font-size: 23px;
        line-height: 36px;
      }
    }

    @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
      p {
        font-size: 31.5px;
        line-height: 50px;
      }
    }
  }

  h3 {
    margin: 1.5rem 0;
  }

  ul,
  ol,
  li {
    margin: 0 0 1rem;

    &:last-of-type {
      margin: 0;
    }
  }

  ul,
  ol {
    margin-left: 1.25rem;
  }
`;

export const Statement = styled.p`
  &.bold {
    font-weight: ${themeGet('fontWeights.bold')};
    font-size: 24px;
    line-height: 30px;
    letter-spacing: -0.15px;

    @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
      font-size: 36px;
      line-height: 40px;
      letter-spacing: -0.2px;
    }

    @media only screen and (min-width: ${themeGet('breakpoints.xlg')}) {
      font-size: 42px;
      line-height: 54px;
      letter-spacing: -0.25px;
    }
  }

  ${StyledContainer}.pcma-list-section & {
    color: ${({ theme }) => theme.colors.white};
    font-size: 1.5rem;
    text-align: center;
    line-height: 2rem;

    @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
      text-align: left;
    }

    @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
      font-size: 2rem;
    }
  }

  ${Block}.red-velvet-gradient & {
    font-family: ${themeGet('fonts.noir')};
    font-size: 18px;
    letter-spacing: 0;
    line-height: 28px;

    @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    }

    @media only screen and (min-width: ${themeGet('breakpoints.xlg')}) {
      font-size: 25px;
      letter-spacing: 0;
      line-height: 39px;
    }
  }
`;

export const Subtitle = styled.div`
  font-size: 1.125rem;
  margin: 0;
  padding: 0;
  font-weight: 500;
`;

export const CTAWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-start;
  flex-wrap: wrap;
  flex-direction: column;
  margin-top: 2rem;

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    justify-content: space-evenly;
    flex-direction: row;
  }

  ${StyledContainer}.pcma-list-section-wrapper & {
    a {
      margin: 0 auto;
      @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
        margin: 0;
      }
    }
  }

  &.stacked {
    flex-direction: column;

    &.left {
      justify-content: flex-start;
      align-items: flex-start;
    }

    a {
      margin-bottom: 1rem;
    }
  }

  &.side-by-side,
  &.side-by-side.content-focus {
    @media only screen and (min-width: 500px) {
      justify-content: flex-start;
    }

    @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
      flex-direction: column;
      align-items: flex-start;
    }

    a {
      margin-bottom: 1.5rem;
    }
  }

  a {
    margin-bottom: 1.5rem;

    @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
      margin-bottom: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
`;
